import {
  Box,
  Dialog,
  Typography,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  ListItemText,
  Button as MuiButton,
  Link,
  Divider,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useEffect, useState } from "react";
import { useUploadLeadsStore } from "../hooks";
import { observer } from "mobx-react";
import { DropzoneArea } from "./components/dropzoneArea";
import { ExcelIcon } from "./components/excelIcon";
import CloseIcon from "@material-ui/icons/Close";
import { BusinessUnit, BusinessUnitArray } from "@roc/feature-utils";
import { useStore } from "@roc/client-portal-shared/hooks";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    width: 480,
    borderRadius: 12,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  uploadButton: {
    marginTop: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
    position: "relative",
  },
  title: {
    fontWeight: 600,
    fontSize: "1.3rem",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  divider: {
    height: 32,
    width: 1,
    backgroundColor: "#ccc",
    margin: "0 16px",
  },
  dropzoneBox: {
    border: "1.5px dashed #3f51b5",
    backgroundColor: "#f3f6f9",
    padding: theme.spacing(3),
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 10,
  },
}));

const SELECT_ALL = "__SELECT_ALL__";

export const UploadLeadsModal = observer(() => {
  const classes = useStyles();
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [eventPlace, setEventPlace] = useState("");
  const { uploadLeadsStore } = useUploadLeadsStore();
  const { salesforceLeadStore } = useStore();
  const [owners, setOwners] = useState([]);

  const isFormValid = () => {
    return (
      file !== null &&
      name.trim() !== "" &&
      brand.trim() !== "" &&
      owners.length > 0 &&
      eventPlace.trim() !== ""
    );
  };

  const handleClose = () => {
    resetForm();
    uploadLeadsStore.setUploadLeadsModalOpen(false);
  };

  const handleDownload = () => {
    uploadLeadsStore.handleDownloadTemplate();
  };

  const resetForm = () => {
    setFile(null);
    setName("");
    setEventPlace("");
    setBrand("");
    setOwners([]);
  };

  const handleUpload = () => {
    uploadLeadsStore.handleUpload(file, name, brand, eventPlace, owners);
  };

  const handleOwnersChange = (e) => {
    const value = e.target.value as string[];
    if (value.includes(SELECT_ALL)) {
      const allOwnerValues = (uploadLeadsStore?.owners || []).map((o) => o.value);
      setOwners(owners.length === allOwnerValues.length ? [] : allOwnerValues);
      return;
    }
    setOwners(value);
  };

  const getAllOwners = async () => {
    if(!(salesforceLeadStore.owners.length > 0)){
      await salesforceLeadStore.getAllOwners();
    }
  };

  const getAllOwnersByBrand = async () => {
    const activeOwners = salesforceLeadStore.getActiveOwnersByBrand(brand);
    uploadLeadsStore.setOwners(activeOwners);
    setOwners([]);
  };

  useEffect(() => {
    getAllOwners();
    resetForm();
  }, [uploadLeadsStore.isUploadLeadsModalOpen]);

  useEffect(() => {
    getAllOwnersByBrand();
  }, [brand]);

  return (
    <Dialog
      open={uploadLeadsStore.isUploadLeadsModalOpen}
      onClose={handleClose}
    >
      <Box className={classes.dialogContent}>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>

        <Box className={classes.header}>
          <Typography className={classes.title}>Upload New File</Typography>
          <Box className={classes.divider} />
          <Link
            onClick={handleDownload}
            target="_blank"
            rel="noopener"
            underline="hover"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <ExcelIcon width={20} height={20} />
            <Typography
              variant="body2"
              style={{ color: "#1565c0", fontWeight: 500 }}
            >
              Download Template
            </Typography>
          </Link>
        </Box>

        <Box className={classes.section}>
          <DropzoneArea
            setFile={setFile}
            file={file}
            store={uploadLeadsStore}
          />
        </Box>

        <Box className={classes.section}>
          <InputLabel shrink>Event</InputLabel>
          <TextField
            placeholder="Event"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>

        <Box className={classes.section}>
          <InputLabel shrink>Event Place</InputLabel>
          <TextField
            placeholder="Event Place"
            fullWidth
            value={eventPlace}
            onChange={(e) => setEventPlace(e.target.value)}
          />
        </Box>

        <Box className={classes.section}>
          <FormControl fullWidth>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brand}
              onChange={(e) => setBrand(e.target.value as string)}
            >
              {BusinessUnitArray.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {brand && (
          <Box className={classes.section}>
            <FormControl fullWidth>
              <InputLabel>Owner Name</InputLabel>
              <Select
                multiple
                value={owners}
                onChange={handleOwnersChange}
                renderValue={(selected) =>
                  (uploadLeadsStore?.owners || [])
                    .filter((o) => (selected as string[]).includes(o.value))
                    .map((o) => o.label)
                    .join(", ")
                }
              >
                <MenuItem value={SELECT_ALL}>
                  <Checkbox
                    checked={
                      uploadLeadsStore?.owners?.length > 0 &&
                      owners.length === uploadLeadsStore.owners.length
                    }
                    indeterminate={
                      owners.length > 0 &&
                      owners.length < (uploadLeadsStore?.owners?.length || 0)
                    }
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {uploadLeadsStore?.owners.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox checked={owners.indexOf(o.value) > -1} />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <MuiButton
          variant="contained"
          color="primary"
          fullWidth
          className={classes.uploadButton}
          onClick={handleUpload}
          disabled={!isFormValid()}
        >
          + Upload File
        </MuiButton>
      </Box>
    </Dialog>
  );
});
